package ui.device

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import common.frontend.api.Io
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.device_listing__search
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_DevicePreviewUI
import tk.mallumo.utils.DataState
import tools.SearchWrapper
import tools.findRoot
import tools.rememberLazyListState
import ui.device.detail.DeviceArgs
import ui.widget.*

data class DeviceListingArgs(
    var companyId: Long = -1,
    var locationId: Long = -1,
    var showAll: Boolean = false
)

@ComposableNavNode(DeviceListingArgs::class)
@Composable
fun DeviceListingUI() {
    with(DeviceListingScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }

    }
}


@OptIn(ExperimentalFoundationApi::class)
@Composable
fun DeviceListingScope.Content(nnvl: DeviceListingScope.Wrapper) {

    val stateHorizontal = rememberScrollState(0)
    val itemMeasurer = headerSpecs.rememberMeasurer()
    val tooltipImageSize = if (LocalDisplayProvider.current.isMobile) 64F else 128F
    val scrollState = nav.rememberLazyListState("devices-${args.companyId}-${args.locationId}")

    Column(
        Modifier.fillMaxSize()
            .padding(top = 8.dp)
            .padding(start = 8.dp)
    ) {
        HomeSearchUI(vm.searchFiled, label = Res.string.device_listing__search)
        Space(8.dp)
        Box(
            Modifier.fillMaxSize()
        ) {
            Box(
                Modifier.fillMaxSize()
                    .tableMeasurer(itemMeasurer)
                    .horizontalScroll(stateHorizontal)
            ) {
                LazyColumn(
                    state = scrollState,
                    contentPadding = PaddingValues(end = 8.dp),
                    modifier = Modifier.fillMaxHeight()
                        .width(itemMeasurer.rowWidth)
                ) {
                    stickyHeader {
                        ListingHeader(headerSpecs)
                    }
                    itemsIndexed(
                        SearchWrapper.use(
                            fields = arrayOf(
                                Io.ListingDevice.Item::id,
                                Io.ListingDevice.Item::idHex,
                                Io.ListingDevice.Item::name,
                                Io.ListingDevice.Item::name_company,
                                Io.ListingDevice.Item::name_location,
                            ),
                            items = nnvl.state,
                            query = vm.searchFiled,
                            sort = compareByDescending {
                                if (it.data.id < 1000) Long.MAX_VALUE - it.data.id else it.data.id
                            }
                        )) { _, item ->
                        ListingDivider()
                        Tooltip(item, itemMeasurer, tooltipImageSize) {
                            ListingItem(
                                item = item,
                                specs = headerSpecs,
                                background = if (item.data.emergencyActive) MaterialTheme.colorScheme.errorContainer
                                else MaterialTheme.colorScheme.surfaceContainer,
                            ) {
                                nav.findRoot().navTo_DevicePreviewUI(DeviceArgs(item.data.id))
                            }
                        }
                    }
                    ListingFooter()
                }
            }
            HorizontalScrollbar(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(bottom = 8.dp)
                    .fillMaxWidth(),
                adapter = rememberScrollbarAdapter(stateHorizontal)
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun Tooltip(
    item: DeviceListingScope.Wrapper.Item,
    itemMeasurer: ListingSpecs.Measurer,
    tooltipImageSize: Float,
    body: @Composable () -> Unit
) {
    Box {
        TooltipBox(
            positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
            tooltip = {
                Box(
                    modifier = Modifier.fillMaxWidth()
                        .offset(x = itemMeasurer.contentBoundary?.left?.dp ?: 0.dp)
                ) {
                    Card {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.height(tooltipImageSize.dp)
                        ) {
                            LazyImage(
                                id = item.data.photo.resourceId,
                                modifier = Modifier
                                    .width(((item.data.photo.width.toFloat() / item.data.photo.height.toFloat()) * tooltipImageSize).dp)
                                    .height(tooltipImageSize.dp)
                            )
                        }
                    }
                }
            },
            state = CustomTooltipState.rememberState(),
            focusable = false,
            enableUserInput = item.data.photo.resourceId > 0,
            content = body
        )
    }
}
